/* eslint-disable camelcase */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  deleteTradingKeyword,
  getTradingKeywords,
  saveTradingKeywords,
  updateTradingKeyword,
} from 'store/modules/monitoring_store/actions';
import { MonitoringState } from 'store/modules/monitoring_store/types';
import {
  getMonitoringPreferences,
  updateMonitoringPreferences,
} from 'store/modules/monitoring_preferences_store/actions';
import { MonitoringPreferencesState } from 'store/modules/monitoring_preferences_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import companyId from 'services/company_id';
import {
  desktopNotificationsBlocked,
  KEYWORD_CHANNELS,
  keywordError,
  MonitoringChannel,
  MonitoringPriority,
  MonitoringRule,
  monitoringRules,
  monitoringSounds,
  PREFERENCE_CHANNEL,
} from 'services/monitoring_settings';
import { playMonitoringSound } from 'services/monitoring_sounds';

const SAVE_ERROR = 'Não foi possível salvar a alteração. Tente novamente.';

const failedResponse = (result: any) => !result || result.status >= 400;

const browserPermission = (): string | undefined =>
  'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window
    ? Notification.permission
    : undefined;

export function useMonitoringSettings() {
  const dispatch = useDispatch();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const { monitoring, request } = useSelector<ApplicationState, MonitoringState>(
    (state) => state.storage.monitoring,
  );
  const preferences = useSelector<ApplicationState, MonitoringPreferencesState>(
    (state) => state.storage.monitoringPreferences,
  );

  const company = companyId(profile);
  const [keyword, setKeyword] = useState('');
  const [keywordMessage, setKeywordMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  const [permission] = useState(browserPermission);
  const mounted = useRef(true);
  const savingPreference = useRef(false);
  const [addingKeyword, setAddingKeyword] = useState(false);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  useEffect(() => {
    setLoadError(false);
    const failed = () => {
      if (mounted.current) setLoadError(true);
    };
    (
      dispatch(
        getMonitoringPreferences(
          profile.representant && company !== profile.clientId ? company : null,
        ),
      ) as unknown as Promise<any>
    ).then((result) => failedResponse(result) && failed(), failed);
    dispatch(getTradingKeywords(company));
  }, [company, dispatch, profile.clientId, profile.representant, reload]);

  const watch = (action: unknown) => {
    setSaveError('');
    const failed = (result?: any) => {
      if (mounted.current) setSaveError(result?.data?.error || SAVE_ERROR);
    };
    return (action as Promise<any>).then(
      (result) => failedResponse(result) && failed(result),
      () => failed(),
    );
  };

  const preference = profile.representant
    ? preferences.monitoring_preferences?.find(({ client_id }) => client_id === company)
    : preferences.monitoring_preferences?.[0];
  const keywords = monitoring.trading_keywords[company];
  const enabled = Boolean(preference?.message_notification);
  const rules = useMemo(() => monitoringRules(preference, keywords), [preference, keywords]);

  const updatePreference = (type: string, values?: { value?: string; priority?: string }) => {
    if (!preference || savingPreference.current) return undefined;

    savingPreference.current = true;
    const done = () => {
      savingPreference.current = false;
    };
    return watch(dispatch(updateMonitoringPreferences(preference.id, type, values))).then(
      done,
      done,
    );
  };

  const changePriority = (rule: MonitoringRule, priority: MonitoringPriority) => {
    if (rule.keywordId === null) updatePreference('all_messages_priority', { value: priority });
    else watch(dispatch(updateTradingKeyword(rule.keywordId, { priority }, company)));
  };

  const toggleChannel = (rule: MonitoringRule, channel: MonitoringChannel) => {
    if (rule.keywordId === null) {
      const saved = updatePreference(PREFERENCE_CHANNEL[channel]);
      if (KEYWORD_CHANNELS.includes(channel)) {
        saved?.then(() => {
          if (mounted.current) dispatch(getTradingKeywords(company));
        });
      }
      return;
    }

    watch(
      dispatch(
        updateTradingKeyword(
          rule.keywordId,
          { [`notify_${channel}`]: !rule.channels[channel] },
          company,
        ),
      ),
    );
  };

  const toggleActive = (rule: MonitoringRule) => {
    if (rule.keywordId === null) updatePreference('receive_notification_from_all_messages');
    else watch(dispatch(updateTradingKeyword(rule.keywordId, { active: !rule.active }, company)));
  };

  const removeKeyword = (rule: MonitoringRule) => {
    if (rule.keywordId !== null) watch(dispatch(deleteTradingKeyword(rule.keywordId, company)));
  };

  const addKeyword = () => {
    if (addingKeyword) return;
    const error = keywordError(keyword, keywords || []);
    if (error) {
      setKeywordMessage(error);
      return;
    }

    setKeywordMessage('');
    const failed = (result?: any) => {
      if (mounted.current) {
        setKeywordMessage(
          result?.data?.error || 'Não foi possível adicionar a palavra-chave. Tente novamente.',
        );
      }
    };
    setAddingKeyword(true);
    (dispatch(saveTradingKeywords(keyword.trim(), company)) as unknown as Promise<any>)
      .then((result) => {
        if (failedResponse(result)) failed(result);
        else if (mounted.current) setKeyword('');
      })
      .catch(() => failed())
      .finally(() => mounted.current && setAddingKeyword(false));
  };

  const changeSound = (priority: MonitoringPriority, sound: string) => {
    updatePreference('sound', { priority, value: sound });
    playMonitoringSound(sound);
  };

  return {
    company,
    loading:
      !loadError &&
      (!preference || (keywords === undefined && request.get_trading_keywords.loading)),
    loadError: loadError && !preference,
    retryLoad: () => setReload((count) => count + 1),
    saveError,
    enabled,
    toggleEnabled: () => updatePreference('message_notification'),
    rules,
    changePriority,
    toggleChannel,
    toggleActive,
    removeKeyword,
    keyword,
    keywordMessage,
    setKeyword: (value: string) => {
      setKeyword(value);
      setKeywordMessage('');
    },
    addKeyword,
    addingKeyword,
    sounds: monitoringSounds(preference),
    changeSound,
    permission,
    desktopBlocked: desktopNotificationsBlocked(enabled, rules, permission),
  };
}

export type MonitoringSettings = ReturnType<typeof useMonitoringSettings>;
